/**
 * Member identity shape shared by both places that push it: the GA4
 * dataLayer (`useAnalyticsIdentity.ts`) and the embedded MSR2 handshake
 * (`useEmbedAuthHandshake.ts`). Lives here, not in either hook, so a plain
 * sign-out path (`managedLoginSignOut`, a non-React function assigned onto
 * `reduxServices.globalState`) can use it without pulling in the hook module
 * it would otherwise sit next to -- and, transitively, `useCurrentUserAuth`
 * -> `stateStore`/Redux -> `AuthHelper`. See `authHelper.ts:35` for the same
 * rule applied one file over.
 */
export interface AnalyticsIdentity {
  // Index signature so this is assignable to setUserProperties's
  // Record<string, unknown> param.
  [key: string]: unknown;
  is_external: boolean;
  user_id: string;
}

// Explicit unset pushed on logout / user going away. GA4 needs this rather
// than silence, otherwise the previous user's identity sticks to the next
// session in the same tab.
export const UNSET_IDENTITY = { is_external: undefined, user_id: undefined };

/**
 * Serialises an identity for change-detection: `null` when there is none,
 * otherwise a stable string suitable for a `===` comparison against a
 * previous render's value.
 *
 * Both `useAnalyticsIdentity` and `useEmbedAuthHandshake` need to tell
 * "identity actually changed" apart from "same identity, just a re-render",
 * and this is the part of that check which is genuinely identical between
 * them. What each hook does once it sees a change -- push to GTM, or decide
 * whether an absence is a meaningful clear worth resending -- differs
 * between the two and is deliberately NOT folded in here; keep that logic
 * where it is.
 */
export const serialiseIdentity = (
  identity: AnalyticsIdentity | null,
): null | string => (identity ? JSON.stringify(identity) : null);
